
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Grid,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Rating,
  Divider,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import RouterIcon from '@mui/icons-material/Router';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SecurityIcon from '@mui/icons-material/Security';
import WarriliLogo from '../assets/logo-white.png'; // Make sure this path is correct

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  minHeight: '100vh',
  paddingBottom: '60px',
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: 'white',
  padding: '15px 0',
}));

const LogoImage = styled('img')({
  height: '40px',
  marginRight: '10px',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: 'white',
  padding: '60px 0',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at center, rgba(255, 230, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%)',
    zIndex: 1,
  }
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
}));

const YellowText = styled('span')(({ theme }) => ({
  color: '#FFE600',
  fontWeight: 'bold',
}));

const YellowButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFE600',
  color: '#000000',
  fontWeight: 'bold',
  padding: '10px 25px',
  '&:hover': {
    backgroundColor: '#FFD600',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  transition: 'all 0.3s ease',
}));

const BlackButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#FFE600',
  fontWeight: 'bold',
  padding: '10px 25px',
  '&:hover': {
    backgroundColor: '#333333',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  transition: 'all 0.3s ease',
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: '40px',
  '& .MuiStepIcon-root.Mui-active': {
    color: '#FFE600',
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: '#FFE600',
  },
}));

const ExpertCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const ExpertAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  margin: '0 auto 15px',
  border: '3px solid #FFE600',
}));

const TimeSlotButton = styled(Button)(({ theme, selected }) => ({
  backgroundColor: selected ? '#FFE600' : '#f5f5f5',
  color: selected ? '#000000' : '#333333',
  border: selected ? 'none' : '1px solid #ddd',
  margin: '5px',
  '&:hover': {
    backgroundColor: selected ? '#FFD600' : '#e0e0e0',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: '20px',
  position: 'relative',
  paddingBottom: '10px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '50px',
    height: '3px',
    backgroundColor: '#FFE600',
  },
}));

const FeatureBox = styled(Paper)(({ theme }) => ({
  padding: '20px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFE600',
  color: '#000000',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '15px',
  '& svg': {
    fontSize: '30px',
  },
}));

const ConfirmationBox = styled(Paper)(({ theme }) => ({
  padding: '30px',
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  textAlign: 'center',
  marginTop: '30px',
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: '60px',
  color: '#FFE600',
  marginBottom: '20px',
}));

// Main component
function VideoConsultation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [issueType, setIssueType] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [description, setDescription] = useState('');
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  
  // Steps for the stepper
  const steps = ['Describe Your Issue', 'Select an Expert', 'Choose Time & Date', 'Confirmation'];
  
  // Sample experts data
  const experts = [
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      specialty: 'Computer Hardware & Software',
      rating: 4.9,
      reviews: 127,
      experience: '8 years',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      availability: true,
    },
    {
      id: 2,
      name: 'Michael Chen',
      specialty: 'Network & Internet Issues',
      rating: 4.7,
      reviews: 93,
      experience: '6 years',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      availability: true,
    },
    {
      id: 3,
      name: 'Jessica Williams',
      specialty: 'Mobile Devices & Tablets',
      rating: 4.8,
      reviews: 105,
      experience: '7 years',
      image: 'https://randomuser.me/api/portraits/women/68.jpg',
      availability: true,
    },
    {
      id: 4,
      name: 'Robert Garcia',
      specialty: 'Smart Home & IoT Devices',
      rating: 4.6,
      reviews: 78,
      experience: '5 years',
      image: 'https://randomuser.me/api/portraits/men/75.jpg',
      availability: false,
    },
  ];
  
  // Sample dates
  const availableDates = [
    '2025-04-08',
    '2025-04-09',
    '2025-04-10',
    '2025-04-11',
    '2025-04-12',
  ];
  
  // Sample time slots
  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', 
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
  ];
  
  // Issue types
  const issueTypes = [
    'Hardware Problem',
    'Software Issue',
    'Network Connectivity',
    'Device Setup',
    'Performance Issues',
    'Security Concern',
    'Other',
  ];
  
  // Device types
  const deviceTypes = [
    'Desktop Computer',
    'Laptop',
    'Smartphone',
    'Tablet',
    'Smart Home Device',
    'Router/Modem',
    'Other',
  ];
  
  // Handle next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Handle expert selection
  const handleSelectExpert = (expert) => {
    setSelectedExpert(expert);
  };
  
  // Handle date selection
  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };
  
  // Handle time selection
  const handleTimeSelection = (time) => {
    setSelectedTime(time);
  };
  
  // Check if current step is complete
  const isStepComplete = () => {
    switch (activeStep) {
      case 0:
        return issueType && deviceType && description;
      case 1:
        return selectedExpert !== null;
      case 2:
        return selectedDate && selectedTime;
      default:
        return true;
    }
  };
  
  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <SectionTitle variant="h5">Tell Us About Your Technical Issue</SectionTitle>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    label="Issue Type"
                  >
                    {issueTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Device Type</InputLabel>
                  <Select
                    value={deviceType}
                    onChange={(e) => setDeviceType(e.target.value)}
                    label="Device Type"
                  >
                    {deviceTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Describe your issue in detail"
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <SectionTitle variant="h5">Select a Technical Expert</SectionTitle>
            <Grid container spacing={3}>
              {experts.map((expert) => (
                <Grid item xs={12} sm={6} md={3} key={expert.id}>
                  <ExpertCard 
                    elevation={selectedExpert?.id === expert.id ? 5 : 1}
                    sx={{ 
                      border: selectedExpert?.id === expert.id ? '2px solid #FFE600' : 'none',
                      opacity: expert.availability ? 1 : 0.6
                    }}
                    onClick={() => expert.availability && handleSelectExpert(expert)}
                  >
                    <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
                      <ExpertAvatar src={expert.image} alt={expert.name} />
                      <Typography variant="h6" gutterBottom>{expert.name}</Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {expert.specialty}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <Rating 
                          value={expert.rating} 
                          precision={0.1} 
                          readOnly 
                          size="small"
                        />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          ({expert.rating})
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {expert.reviews} reviews
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Experience: {expert.experience}
                      </Typography>
                      <Chip 
                        label={expert.availability ? "Available" : "Unavailable"} 
                        color={expert.availability ? "success" : "default"}
                        size="small"
                        sx={{ mt: 2 }}
                      />
                    </CardContent>
                  </ExpertCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            <SectionTitle variant="h5">Schedule Your Consultation</SectionTitle>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Select Date
                  </Typography>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Available Dates</InputLabel>
                    <Select
                      value={selectedDate}
                      onChange={handleDateChange}
                      label="Available Dates"
                    >
                      {availableDates.map((date) => (
                        <MenuItem key={date} value={date}>
                          {new Date(date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Select Time
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                    {timeSlots.map((time) => (
                      <TimeSlotButton
                        key={time}
                        selected={selectedTime === time}
                        onClick={() => handleTimeSelection(time)}
                        variant={selectedTime === time ? "contained" : "outlined"}
                      >
                        {time}
                      </TimeSlotButton>
                    ))}
                  </Box>
                </Paper>
              </Grid>
              {selectedExpert && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Your Selected Expert
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={selectedExpert.image} alt={selectedExpert.name} sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedExpert.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedExpert.specialty}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      case 3:
        return (
          <ConfirmationBox>
            <SuccessIcon />
            <Typography variant="h4" gutterBottom>
              Your Consultation is Scheduled!
            </Typography>
            <Typography variant="body1" paragraph>
              We've sent a confirmation email with all the details and instructions for joining the video call.
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 3, textAlign: 'left' }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Consultation Details
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarMonthIcon sx={{ mr: 1, color: '#FFE600' }} />
                  <Typography>
                    {selectedDate && new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: '#FFE600' }} />
                  <Typography>{selectedTime}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: '#FFE600' }} />
                  <Typography>{selectedExpert?.name} - {selectedExpert?.specialty}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Issue Information
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Issue Type:</strong> {issueType}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Device Type:</strong> {deviceType}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {description}
                </Typography>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <YellowButton 
                variant="contained" 
                startIcon={<CalendarMonthIcon />}
                component={Link}
                to="/dashboard"
              >
                Go to My Appointments
              </YellowButton>
              <BlackButton 
                variant="contained"
                component={Link}
                to="/"
              >
                Return to Home
              </BlackButton>
            </Box>
          </ConfirmationBox>
        );
      default:
        return 'Unknown step';
    }
  };
  
  return (
    <PageContainer>
      <Header>
        <Container>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <LogoImage src={WarriliLogo} alt="Warrili Logo" />
            </Link>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2, color: 'white' }}>
              Video Consultation
            </Typography>
            <Button 
              component={Link} 
              to="/"
              sx={{ color: 'white' }}
            >
              Back to Home
            </Button>
          </Box>
        </Container>
      </Header>
      
      <HeroSection>
        <Container>
          <HeroContent>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h3" gutterBottom>
                  Expert Technical Support <YellowText>Via Video Call</YellowText>
                </Typography>
                <Typography variant="body1" paragraph>
                  Get face-to-face assistance from our certified technical experts to solve your complex issues quickly and efficiently.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <YellowButton 
                    variant="contained" 
                    size="large"
                    startIcon={<VideoCallIcon />}
                    onClick={() => window.scrollTo({top: 500, behavior: 'smooth'})}
                  >
                    Schedule Now
                  </YellowButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                <Box 
                  component="img"
                  src="https://img.freepik.com/free-vector/video-conference-remote-working-flat-illustration_88138-415.jpg"
                  alt="Video Consultation"
                  sx={{ 
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '10px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </Grid>
            </Grid>
          </HeroContent>
        </Container>
      </HeroSection>
      
      <Container sx={{ mt: -5, position: 'relative', zIndex: 10 }}>
        <Paper sx={{ p: 4, borderRadius: '10px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}>
          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>
          
          {getStepContent(activeStep)}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <YellowButton
              variant="contained"
              onClick={handleNext}
              endIcon={activeStep === steps.length - 1 ? null : <ArrowForwardIcon />}
              disabled={!isStepComplete()}
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </YellowButton>
          </Box>
        </Paper>
      </Container>
      
      <Container sx={{ mt: 8 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Why Choose Our <YellowText>Video Consultation</YellowText> Service?
        </Typography>
        <Typography variant="body1" align="center" paragraph sx={{ mb: 5 }}>
          Get personalized technical support from the comfort of your home or office
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <ComputerIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Screen Sharing
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Share your screen with our experts to show them exactly what's happening on your device.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <HeadsetMicIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Certified Experts
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Our technical specialists are certified professionals with years of experience.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <SecurityIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Secure Connection
              </Typography>
              <Typography variant="body2" color="textSecondary">
                All video consultations are encrypted and secure to protect your privacy.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <PhoneAndroidIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Mobile Support
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Get help with smartphones, tablets, and other mobile devices from our specialists.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <RouterIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Network Assistance
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Resolve connectivity issues, router setup, and network configuration problems.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureBox elevation={2}>
              <IconWrapper>
                <TabletMacIcon />
              </IconWrapper>
              <Typography variant="h6" gutterBottom>
                Multi-Device Support
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Get help with computers, laptops, tablets, smartphones, and smart home devices.
              </Typography>
            </FeatureBox>
          </Grid>
        </Grid>
      </Container>
      
      <Container sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4, backgroundColor: '#000000', color: 'white', borderRadius: '10px' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                Need Immediate Assistance?
              </Typography>
              <Typography variant="body1">
                If you have an urgent technical issue, schedule a video consultation now and get help from our experts within minutes.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <YellowButton 
                variant="contained" 
                size="large"
                startIcon={<VideoCallIcon />}
                onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                fullWidth={isMobile}
              >
                Schedule Urgent Consultation
              </YellowButton>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </PageContainer>
  );
}

export default VideoConsultation;